import clsx from "clsx";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import React, {
  ReactNode,
  memo,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

interface ISlideDownUpAnimation {
  children: ReactNode;
  show?: boolean;
  keepAlive?: boolean;
}

const SlideDownUpAnimation: React.FC<ISlideDownUpAnimation> = ({
  children,
  show = true,
  keepAlive = false,
}) => {
  const prefersReducedMotion = useReducedMotion();
  const contentRef = useRef<HTMLDivElement>(null);

  // height puede ser number (px) durante animación o "auto" cuando está abierto
  const [height, setHeight] = useState<number | "auto">(show ? "auto" : 0);
  const [rendered, setRendered] = useState(show); // para keepAlive

  // Asegura que cuando show=true y keepAlive, el contenido exista
  useEffect(() => {
    if (show) setRendered(true);
  }, [show]);

  const measure = () => {
    const el = contentRef.current;
    if (!el) return 0;
    // scrollHeight no se ve afectado por el overflow/height del padre
    return el.scrollHeight;
  };

  // Re-medir en cambios de children SOLO cuando está abierto (o renderizado)
  useLayoutEffect(() => {
    if (!rendered) return;
    if (show) {
      // si está abierto y height está en "auto", no hace falta setear.
      // pero si está en número (por una animación), ajusta al valor real.
      const h = measure();
      if (height !== "auto") setHeight(h);
    }
  }, [children, rendered, show]);

  // ResizeObserver: útil si el contenido cambia de tamaño por imágenes, fonts, etc.
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const ro = new ResizeObserver(() => {
      if (!show) return; // clave: NO re-medimos mientras colapsa
      const h = measure();
      // si estamos en px (por animación), actualiza; si está en auto, lo dejamos.
      if (height !== "auto") setHeight(h);
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, [show, height]);

  // Cuando show cambia, prepara la animación sin saltos
  useLayoutEffect(() => {
    if (!rendered) return;

    if (show) {
      // abrir: fija height en px para poder animar (de 0 -> px)
      const h = measure();
      setHeight(h);
    } else {
      // cerrar: fija height actual en px antes de ir a 0
      const h = measure();
      setHeight(h);
      // en el próximo frame bajamos a 0 (garantiza transición suave)
      requestAnimationFrame(() => setHeight(0));
    }
  }, [show, rendered]);

  const onComplete = () => {
    if (show) {
      // al abrir: deja auto para que crezca sin re-animar
      setHeight("auto");
    } else {
      // al cerrar: si keepAlive, podés ocultar sin desmontar
      if (keepAlive) setRendered(false);
    }
  };

  const panel = (
    <motion.div
      key="slide"
      style={{ overflow: "hidden" }}
      {...(!prefersReducedMotion && {
        initial: false,
        animate: { opacity: show ? 1 : 0, height },
        exit: { opacity: 0, height: 0 },
        transition: { duration: 0.3, ease: "easeOut" },
      })}
      onAnimationComplete={onComplete}
      className={clsx({ hidden: keepAlive && !rendered })}
    >
      <div ref={contentRef} className="pt-px">
        {children}
      </div>
    </motion.div>
  );

  if (keepAlive) return panel;

  return <AnimatePresence>{(show || rendered) && panel}</AnimatePresence>;
};

export default memo(SlideDownUpAnimation);
